import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money2, timeAgo } from "@/lib/api";
import { SectionTitle, Thumb, Pill, EmptyState } from "@/components/Bits";

const TONE = {
  "Needs Review": "bg-[#F6DED6] text-[#A8492E]",
  "Alternative Requested": "bg-[#EDEFC4] text-ink",
  Approved: "bg-[#BEC221] text-ink",
  Responded: "bg-[#DDE3C8] text-ink",
  Resolved: "bg-line text-stone",
  Superseded: "bg-line text-fog",
};

const FILTERS = [
  ["open", "All Open"],
  ["awaiting", "Awaiting Client Decision"],
  ["alternative", "Alternative Requested"],
  ["question", "Client Question"],
  ["completed", "Completed Decisions"],
];

export default function ProjectDecisions({ projectId }) {
  const [filter, setFilter] = useState("open");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    const scope = filter === "completed" ? "completed" : "open";
    api
      .get(`/projects/${projectId}/decisions`, { params: { scope } })
      .then((r) => setRows(r.data))
      .catch(() => setRows([]));
  }, [projectId, filter]);

  const shown = rows.filter((d) => {
    if (filter === "awaiting") return d.status === "Needs Review" && d.type !== "question_response";
    if (filter === "alternative") return d.status === "Alternative Requested";
    if (filter === "question") return d.type === "question_response";
    return true;
  });

  return (
    <div className="space-y-8" data-testid="project-decisions">
      <SectionTitle>Client Decisions</SectionTitle>
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([value, label]) => (
          <button
            key={value}
            data-testid={`project-decisions-filter-${value}`}
            onClick={() => setFilter(value)}
            className={`rounded-sm border px-3 py-1.5 text-xs transition-colors ${
              filter === value ? "border-lime bg-lime text-ink" : "border-line bg-white text-stone hover:border-fog"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <EmptyState testId="project-decisions-empty" title="Nothing here" body="No decisions match this filter right now." />
      ) : (
        <div className="card-surface" data-testid="project-decisions-list">
          {shown.map((d) => (
            <Link
              key={d.id}
              to={d.link || "#"}
              data-testid={`project-decision-${d.id}`}
              className="flex flex-wrap items-center gap-5 border-b border-line px-6 py-4 transition-colors last:border-0 hover:bg-bone"
            >
              <Thumb src={d.image} alt={d.title} className="h-12 w-14" />
              <div className="min-w-0 flex-1">
                <div className="text-sm text-ink">{d.title}</div>
                <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-fog">
                  <span>{d.type_label}</span>
                  {d.space_name && <span>{d.space_name}</span>}
                  {d.price ? <span className="num">{money2(d.price)}</span> : null}
                  <span className="num">{timeAgo(d.created_at)}</span>
                </div>
              </div>
              <Pill tone={TONE[d.status] || "bg-line text-stone"}>{d.status}</Pill>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
